import { IStyle, ITheme, mergeStyles } from '@fluentui/react';
import { HostAppColors } from './HostAppColors';
import { ThemeColorPalette, ShadowStyles, FluentV9ColorTokens, FluentV9FontFamilyTokens } from '@fluidx/component-ux';

function isV8Theme(maybeTheme: object): maybeTheme is ITheme {
  const theme = maybeTheme as { palette?: unknown; semanticColors?: unknown };
  return Boolean(theme.palette && theme.semanticColors);
}

/**
 * Returns override styles for Fluent UI v8 primary buttons.
 *
 * @param appColors - The host theme object to use.
 * If `HostAppColors` is provided, the button styles will use theme colors for its background.
 *
 * Otherwise, the button falls back to using Fluent UI v9 design tokens.
 * A v8 `ITheme` is used to obtain fallback values.
 */
export function getPrimaryButtonStyle(appColors: HostAppColors | ITheme) {
  const disabledBackground = `var(${FluentV9ColorTokens.colorNeutralForegroundDisabled}, ${ThemeColorPalette.Neutral.neutralLighter})`;
  const baseStyles: IStyle = {
    fontFamily: `var(${FluentV9FontFamilyTokens.fontFamilyBase}, 'Segoe UI Semibold', 'Segoe UI Web (West European)', 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif)`,
    boxShadow: ShadowStyles.shadowLevel4,
    selectors: {
      ':disabled': {
        color: `var(${FluentV9ColorTokens.colorNeutralForegroundDisabled}, #BEBBB8)`,
        background: disabledBackground,
        borderColor: disabledBackground
      }
    }
  };

  let background: string;
  let hoverBackground: string;
  let pressedBackground: string;
  if (isV8Theme(appColors)) {
    const { semanticColors } = appColors;
    background = `var(${FluentV9ColorTokens.colorBrandBackground}, ${semanticColors.primaryButtonBackground})`;
    hoverBackground = `var(${FluentV9ColorTokens.colorBrandBackgroundHover}, ${semanticColors.primaryButtonBackgroundHovered})`;
    pressedBackground = `var(${FluentV9ColorTokens.colorBrandBackgroundPressed}, ${semanticColors.primaryButtonBackgroundPressed})`;
  } else {
    background = appColors.themePrimary;
    hoverBackground = appColors.themeDarkAlt;
    pressedBackground = appColors.themeDark;
  }

  return mergeStyles(baseStyles, {
    backgroundColor: background,
    borderColor: background,
    selectors: {
      ':hover': {
        background: hoverBackground,
        borderColor: hoverBackground
      },
      ':active': {
        background: pressedBackground,
        borderColor: pressedBackground
      }
    }
  });
}
